import type { LeadRepository } from '../../application/LeadRepository'
import { isLeadContactable, type LeadDataSource, type LeadStats } from '../../domain/lead'
import { leadsFixtures } from './leadsFixtures'

export function createFixtureLeadRepository(source: LeadDataSource = 'fixtures'): LeadRepository {
  const records = [...leadsFixtures]
  return {
    list(filters) {
      const search = filters.search?.trim().toLocaleLowerCase('fr-CA')
      const filtered = records.filter((lead) => {
        if (filters.status && lead.status !== filters.status) return false
        if (!search) return true
        return [lead.number, lead.contact.displayName, lead.contact.email, lead.contact.phone]
          .filter(Boolean)
          .some((value) => value?.toLocaleLowerCase('fr-CA').includes(search))
      })
      const start = (filters.page - 1) * filters.pageSize
      const count = (status: (typeof leadsFixtures)[number]['status']) =>
        records.filter((lead) => lead.status === status).length
      const stats: LeadStats = {
        total: records.length,
        new: count('NEW'),
        contacted: count('CONTACTED'),
        quoteSent: count('QUOTE_SENT'),
        won: count('WON'),
        lost: count('LOST'),
      }
      return Promise.resolve({
        items: filtered.slice(start, start + filters.pageSize),
        page: filters.page,
        pageSize: filters.pageSize,
        total: filtered.length,
        stats,
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    getByReference(reference) {
      const lead = records.find((item) => item.id === reference || item.number === reference)
      return Promise.resolve(
        lead
          ? {
              lead,
              // Aucune Soumission n'est simulée pour un Lead de démonstration.
              quotes: [],
              meta: {
                source,
                retrievedAt: new Date().toISOString(),
                anomalies: isLeadContactable(lead) ? 0 : 1,
              },
            }
          : null,
      )
    },
    create() {
      return Promise.reject(new Error('LEAD_CREATE_SUPABASE_REQUIRED'))
    },
    changeStatus() {
      return Promise.reject(new Error('LEAD_STATUS_SUPABASE_REQUIRED'))
    },
    updateAddress() {
      return Promise.reject(new Error('LEAD_ADDRESS_SUPABASE_REQUIRED'))
    },
    findDuplicates() {
      // Aucune suggestion n'est simulée : elles dépendent des Clients réels.
      return Promise.resolve([])
    },
    convert() {
      return Promise.reject(new Error('LEAD_CONVERSION_SUPABASE_REQUIRED'))
    },
    completeReminder() {
      return Promise.reject(new Error('LEAD_REMINDER_SUPABASE_REQUIRED'))
    },
    recordCallOutcome() {
      return Promise.reject(new Error('LEAD_CALL_OUTCOME_SUPABASE_REQUIRED'))
    },
    getAttribution() {
      // Aucune provenance fictive : une démonstration montre l'état « Inconnue ».
      return Promise.resolve(null)
    },
    setDeclaredSource() {
      return Promise.reject(new Error('LEAD_ATTRIBUTION_SUPABASE_REQUIRED'))
    },
    delete() {
      return Promise.reject(new Error('LEAD_DELETE_SUPABASE_REQUIRED'))
    },
    geocodeAddress() {
      return Promise.reject(new Error('LEAD_GEOCODE_SUPABASE_REQUIRED'))
    },
  }
}
